import { useState, useCallback } from 'react';
import { useAppContext } from '@/context/AppContext';
import { LuaVehicleParser } from '@/parsers/LuaVehicleParser';
import { VehicleModel } from '@/models/Vehicle';
import { FileManagerState, FileManagerActions } from '@/types';

export function useFileManager(): [FileManagerState, FileManagerActions] {
  const { state, dispatch } = useAppContext();
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [luaFile, setLuaFile] = useState<File | null>(null);

  const fileManagerState: FileManagerState = {
    xmlFile,
    luaFile,
    xmlDoc: state.xmlDoc,
    vehicleData: state.vehicleData,
    loadingStates: state.ui.loadingStates
  };

  const showFilePicker = useCallback((accept: string): Promise<File | null> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = (e) => {
        const target = e.target as HTMLInputElement;
        resolve(target.files?.[0] || null);
      };
      input.click();
    });
  }, []);

  const readFileContent = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }, []);

  const fetchSampleContent = useCallback(async (path: string): Promise<string> => {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to fetch sample file: ${response.status}`);
    }
    return response.text();
  }, []);

  const loadXMLFile = useCallback(async (): Promise<Document | null> => {
    try {
      const file = await showFilePicker('.xml');
      if (!file) return null;

      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: true } });
      dispatch({ type: 'CLEAR_ERRORS' });

      const content = await readFileContent(file);
      
      // Parse XML content
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML format');
      }

      setXmlFile(file);
      dispatch({ type: 'SET_XML_DOC', payload: xmlDoc });
      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: false } });
      
      return xmlDoc;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: false } });
      dispatch({ 
        type: 'ADD_ERROR', 
        payload: `Error loading XML file: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
      return null;
    }
  }, [showFilePicker, readFileContent, dispatch]);

  const loadSampleXMLFile = useCallback(async (): Promise<Document | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: true } });
      dispatch({ type: 'CLEAR_ERRORS' });

      const content = await fetchSampleContent('/popgroups.ymt.pso.xml');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'text/xml');

      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML format');
      }

      setXmlFile(new File([content], 'popgroups.ymt.pso.xml', { type: 'text/xml' }));
      dispatch({ type: 'SET_XML_DOC', payload: xmlDoc });
      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: false } });

      return xmlDoc;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: { type: 'xml', loading: false } });
      dispatch({
        type: 'ADD_ERROR',
        payload: `Error loading sample XML file: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      return null;
    }
  }, [dispatch, fetchSampleContent]);

  const loadLuaFile = useCallback(async (): Promise<VehicleModel[] | null> => {
    try {
      const file = await showFilePicker('.lua');
      if (!file) return null;

      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: true } });
      dispatch({ type: 'CLEAR_ERRORS' });

      const content = await readFileContent(file);
      
      // Parse Lua content
      const luaParser = new LuaVehicleParser();
      const vehicles = luaParser.parseVehicleFile(content);
      
      setLuaFile(file);
      dispatch({ type: 'SET_VEHICLE_DATA', payload: vehicles.map(v => v.toJSON()) });
      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: false } });
      
      return vehicles;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: false } });
      dispatch({ 
        type: 'ADD_ERROR', 
        payload: `Error loading Lua file: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
      return null;
    }
  }, [showFilePicker, readFileContent, dispatch]);

  const loadSampleLuaFile = useCallback(async (): Promise<VehicleModel[] | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: true } });
      dispatch({ type: 'CLEAR_ERRORS' });

      const content = await fetchSampleContent('/vehicles.lua');
      const luaParser = new LuaVehicleParser();
      const vehicles = luaParser.parseVehicleFile(content);

      setLuaFile(new File([content], 'vehicles.lua', { type: 'text/plain' }));
      dispatch({ type: 'SET_VEHICLE_DATA', payload: vehicles.map(v => v.toJSON()) });
      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: false } });

      return vehicles;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: { type: 'lua', loading: false } });
      dispatch({
        type: 'ADD_ERROR',
        payload: `Error loading sample Lua file: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      return null;
    }
  }, [dispatch, fetchSampleContent]);

  const downloadFile = useCallback((content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const saveXMLFile = useCallback(async (): Promise<boolean> => {
    try {
      if (!state.xmlDoc) {
        dispatch({ type: 'ADD_ERROR', payload: 'No XML file loaded!' });
        return false;
      }

      const serializer = new XMLSerializer();
      const xmlContent = serializer.serializeToString(state.xmlDoc);

      downloadFile(xmlContent, 'popgroups.ymt.pso.xml', 'text/xml');

      dispatch({ type: 'MARK_SAVED' });
      dispatch({ type: 'ADD_SUCCESS', payload: 'File saved successfully!' });
      return true;
    } catch (error) {
      dispatch({ 
        type: 'ADD_ERROR', 
        payload: `Error saving file: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
      return false;
    }
  }, [state.xmlDoc, downloadFile, dispatch]);

  const checkUnsavedChanges = useCallback((): boolean => {
    if (state.hasUnsavedChanges) {
      return window.confirm('You have unsaved changes. Continue anyway?');
    }
    return true;
  }, [state.hasUnsavedChanges]);

  const fileManagerActions: FileManagerActions = {
    loadXMLFile,
    loadLuaFile,
    loadSampleXMLFile,
    loadSampleLuaFile,
    saveXMLFile,
    checkUnsavedChanges
  };

  return [fileManagerState, fileManagerActions];
}
